import React, { useState } from "react";
import { Alert, Modal } from "react-bootstrap";
import axios from "axios";
import useAxiosData from "~/shared/use-axios-data";
import useModal from "~/shared/UseModal";
import DataTable from "~/shared/DataTable";
import TextInput from "~/shared/form/TextInput";
import ControlledForm from "~/shared/form/ControlledForm";
import FormResponse from "~/shared/form/FormResponse";
import FormButton from "~/shared/form/FormButton";
import FormSection from "~/shared/form/FormSection";
import { useForm } from "react-hook-form";
import { localeFormatDate } from "~/shared/utils";
import SelectInput from "~/shared/form/SelectInput";
import CheckboxInput from "~/shared/form/CheckboxInput";
import FileInput from "~/shared/form/FileInput";
import FileView from "~/shared/form/FileView";
import AlertBox from "~/shared/AlertBox";

export const CERTIFICATION_TYPES = ["Mandated Reporter", "CPR Certification", "Teaching Certification", "Training Certificate(s)", "Background Check"];

export default function ManageCertifications() {
    const [certifications, status, setCertifications] = useAxiosData(() => axios.get("staff/certifications/get"), []);
    const [showModal, setShowModal] = useState(false);
    const [editingCertification, setEditingCertification] = useState(null);
    const [editing, setEditing] = useState(false);
    const { ModalDialog, showConfirm } = useModal();
    const formHook = useForm({ disabled: !editing });

    function onSubmit(data) {
        if (data.type === "Other") {
            data.type = data.typeOther;
            delete data.typeOther;
        }
    }

    function onSuccess({ _id }, _, data) {
        setShowModal(false);

        if (editingCertification) {
            setCertifications(certifications.map(c => c._id === editingCertification._id ? { ...data, _id: c._id, status: "Submitted" } : c));
        } else {
            setCertifications([...certifications, { ...data, _id, status: "Submitted" }]);
        }

        // Wait for modal to fade out
        setTimeout(() => formHook.reset(undefined, { keepErrors: true }), 150);
    }

    async function deleteCertification(certification, context) {
        if (!await showConfirm("Delete Certification", "Are you sure you want to delete this certification?")) {
            return;
        }

        context.setLoading(true);
        await axios.post(`staff/certifications/delete/${certification._id}`);
        setCertifications(c => c.filter(c => c._id !== certification._id));
    }

    function openCertification(certification, edit) {
        setEditing(edit);
        setEditingCertification(certification);
        setShowModal(true);

        if (!certification) {
            formHook.reset();
            return;
        }

        if (!CERTIFICATION_TYPES.includes(certification.type)) {
            formHook.setValue("type", "Other");
            formHook.setValue("typeOther", certification.type);
        } else {
            formHook.setValue("type", certification.type);
        }

        formHook.setValue("dateReceived", certification.dateReceived.split('T')[0]);
        formHook.setValue("expirationDate", certification.expirationDate.split('T')[0]);
        formHook.setValue("document", { name: certification.document.name });

        setEditingCertification({ ...editingCertification });
    }

    function getCertificationActions(certification) {
        return <>
            <DataTable.Action onClick={() => openCertification(certification, certification.status === "Rejected")}>{certification.status === "Rejected" ? "Edit" : "View"}</DataTable.Action>
            <DataTable.Action onClick={(_, context) => deleteCertification(certification, context)} manualSpinner>Delete</DataTable.Action>
        </>;
    }

    return (
        <section>
            {ModalDialog}

            <DataTable data={certifications} loading={status === "loading"}>
                <DataTable.Column id="type" sortable>Certification Type</DataTable.Column>
                <DataTable.Column id="dateReceived" format={localeFormatDate} sortable>Date</DataTable.Column>
                <DataTable.Column id="expirationDate" format={localeFormatDate} sortable>Expires On</DataTable.Column>
                <DataTable.Column id="completed" format={c => c ? "Yes" : "No"} sortable>Completed</DataTable.Column>
                <DataTable.Column id="status" sortable>Status</DataTable.Column>
                <DataTable.Column value={getCertificationActions}>Actions</DataTable.Column>
                <DataTable.Message condition={status === "error"}>
                    <AlertBox type="error">An unknown error occurred. Please try again later.</AlertBox>
                </DataTable.Message>
                <DataTable.Message condition={certifications.length === 0}>
                    <div className="text-center text-muted my-4">No certifications have been added yet.</div>
                </DataTable.Message>
            </DataTable>

            <Modal show={showModal} onHide={() => setShowModal(false)} className="fixed-size-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{editingCertification ? (editing ? "Edit Certification" : "View Certification") : "Add New Certification"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ControlledForm hook={formHook} onSubmit={onSubmit} post={editingCertification ? `staff/certifications/update/${editingCertification._id}` : "staff/certifications/add"} onSuccess={onSuccess}>
                        <div className="flex flex-col gap-3">
                            <FormSection row wrap gap={1}>
                                <SelectInput label="Type" id="type" options={[...CERTIFICATION_TYPES, "Other"]} maxWidth={formHook.watch("type") === "Other" ? 3 : undefined} placeholder="Select type" required />
                                {formHook.watch("type") === "Other" && <TextInput label="Certification Type" id="typeOther" required />}
                            </FormSection>
                            <FormSection row wrap gap={1}>
                                <TextInput label="Date Received" id="dateReceived" type="date" required />
                                <TextInput label="Expiration Date" id="expirationDate" type="date" required validate={v => new Date(`${v}T23:59:59`) > new Date()} invalid="Expiration date must be in the future." />
                            </FormSection>
                            {formHook.formState.disabled ? (
                                <FileView id="document" label="Certification Document" />
                            ) : (
                                <FileInput id="document" label="Certification Document" extensions={["PDF"]} required />
                            )}
                            <CheckboxInput id="completed" label="Completed Certification" boldLabel />
                        </div>
                        <FormResponse />
                        {editingCertification ? <div className="d-flex flex-wrap justify-content-center gap-5">
                            {editing ? <>
                                <FormButton legacy submit className="mx-0">Resubmit</FormButton>
                                <FormButton legacy className="mx-0" onClick={() => setShowModal(false)}>Cancel</FormButton>
                            </> : <FormButton legacy className="mx-0" onClick={() => setShowModal(false)}>Close</FormButton>}
                        </div> : <FormButton legacy submit />}
                    </ControlledForm>
                </Modal.Body>
            </Modal>

            <button className="large-button mt-3" onClick={() => openCertification(null, true)} disabled={status !== "success"}>
                Create Certification
            </button>
        </section>
    );
}